import { observer } from 'mobx-react-lite';
import { ArrowsClockwise, DownloadSimple, ShoppingCart } from '@phosphor-icons/react';
import { useResolve } from '@/infrastructure/useResolve';
import { useT } from './useT';
import { epochDayIso } from '@/domain/beforered';
import { TOKEN_LocaleStore } from './LocaleStore';
import { TOKEN_ResultsStore } from './ResultsStore';
import { TOKEN_SimulationStore } from './SimulationStore';

const cellHead =
  'border-b border-steel-300 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-steel-500 dark:border-steel-700';
const cell = 'border-b border-steel-100 px-2 py-1 dark:border-steel-900';

export const ResultsView = observer(function ResultsView() {
  const results = useResolve(TOKEN_ResultsStore);
  const sim = useResolve(TOKEN_SimulationStore);
  const locale = useResolve(TOKEN_LocaleStore);
  const t = useT();
  if (!results.hasResult) return null;

  const recs = results.recommendations;
  const nf = new Intl.NumberFormat(locale.current);

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center justify-between border-b-2 border-steel-300 bg-white px-4 py-3 dark:border-steel-700 dark:bg-steel-900">
        <div className="flex items-center gap-3">
          <ShoppingCart weight="duotone" size={22} className="text-amber-signal" />
          <div className="font-mono text-sm">
            <strong className="text-lg">{nf.format(recs.length)}</strong>
            <span className="ml-1 text-steel-500">
              {t.results.orders} · {Math.round(sim.durationMs)}MS
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={sim.reset}
            className="flex items-center gap-1 border border-steel-300 bg-white px-3 py-1.5 font-mono text-xs uppercase tracking-wider hover:bg-steel-50 dark:border-steel-700 dark:bg-steel-900 dark:hover:bg-steel-800"
          >
            <ArrowsClockwise size={14} />
            {t.results.newRun}
          </button>
          <button
            type="button"
            onClick={() => void results.download()}
            disabled={recs.length === 0}
            className="flex items-center gap-1 bg-amber-signal px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-wider text-steel-950 hover:bg-amber-deep hover:text-white disabled:opacity-30"
          >
            <DownloadSimple weight="bold" size={14} />
            {t.results.downloadCsv}
          </button>
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-auto bg-white px-4 py-3 dark:bg-steel-950">
        {recs.length === 0 ? (
          <p className="font-mono text-sm text-steel-500">{t.results.nothingToBuy}</p>
        ) : (
          <table className="w-full border-collapse font-mono text-xs">
            <thead>
              <tr>
                <th className={`${cellHead} text-left`}>{t.results.colOrderBy}</th>
                <th className={`${cellHead} text-left`}>{t.results.colSku}</th>
                <th className={`${cellHead} text-left`}>{t.results.colDescription}</th>
                <th className={`${cellHead} text-right`}>{t.results.colHave}</th>
                <th className={`${cellHead} text-right`}>{t.results.colNeed}</th>
                <th className={`${cellHead} text-left`}>{t.results.colUom}</th>
                <th className={`${cellHead} text-left`}>{t.results.colNeedBy}</th>
                <th className={`${cellHead} text-left`}>{t.results.colDriving}</th>
              </tr>
            </thead>
            <tbody>
              {recs.map((r) => (
                <tr key={r.sku} className="hover:bg-amber-signal/5">
                  <td className={cell}>{epochDayIso(r.orderByDate)}</td>
                  <td className={`${cell} font-bold`}>{r.sku}</td>
                  <td className={cell}>{r.description}</td>
                  <td className={`${cell} text-right text-steel-500`}>
                    {nf.format(r.currentOnHand)}
                  </td>
                  <td className={`${cell} text-right font-bold text-amber-deep`}>
                    {nf.format(Number(r.suggestedQuantity.toFixed(2)))}
                  </td>
                  <td className={cell}>{r.uom}</td>
                  <td className={cell}>{epochDayIso(r.neededByDate)}</td>
                  <td className={`${cell} text-steel-500`}>{r.drivingOrders.join('; ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
});
